import { useRef } from "react";
import type { MatchVm } from "../tournamentPage/matchVm";
import { buildCompact, type Compact } from "./compact";
import { infoFor, matchUrl, type EmbedNav } from "./info";
import type { EmbedLayout } from "./layout";
import { useReportHeight } from "./useReportHeight";
import logo from "../../assets/images/criccenter_logo.png";
import "./headerWidget.scss";

type Props = { layout: Exclude<EmbedLayout, "card">; vm: MatchVm; now: number; nav?: EmbedNav };

const WORD = { live: "Live", upcoming: "Upcoming", result: "Result" } as const;
const ariaFor = (c: Compact) =>
  `Match ${c.no}, ${WORD[c.state]}. ${c.teams[0].code} versus ${c.teams[1].code}. ${c.label}. ${c.state === "upcoming" ? "Open match center" : "Open scorecard"}`;

const Logo = () => <img className="cc-logo" src={logo} alt="CricCenter" width="31" height="31" />;
const Dot = ({ c }: { c: Compact }) => <span className={`dot dot--${c.state}`} aria-hidden="true" />;
const Chevron = ({ small }: { small?: boolean }) => (
  <svg viewBox={small ? "0 0 5 8" : "0 0 8 12"} fill="none" stroke="currentColor" strokeWidth={small ? 1.5 : 1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={small ? "M1 1l3 3-3 3" : "M2 2l4 4-4 4"} />
  </svg>
);

/** "HOU 168/4 20   NYX 121/3" for a started match, "NYX v CHI" before it. */
const ScoreLine = ({ c, overs = true, opponent = true }: { c: Compact; overs?: boolean; opponent?: boolean }) => {
  if (!c.lead.score) return <span className="s-fixture">{c.teams[0].code}<i>v</i>{c.teams[1].code}</span>;
  return (
    <>
      <span className="s-code">{c.lead.code}</span>
      <span className="s-score">{c.lead.score}</span>
      {overs && c.lead.overs && <span className="s-overs">{c.lead.overs}</span>}
      {opponent && <span className="s-other">{c.other.score ? `${c.other.code} ${c.other.score}` : `v ${c.other.code}`}</span>}
    </>
  );
};

/** The three small layouts for a site header. Links open the match page of the full site in a new tab. */
const EmbedHeaderWidget = ({ layout, vm, now, nav }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  useReportHeight(ref);
  const c = buildCompact(vm, now, infoFor(vm, now).main);
  const href = matchUrl(vm.id);
  const aria = ariaFor(c);
  const cycle = nav && nav.total > 1 ? nav.onCycle : undefined;
  const link = { href, target: "_blank", rel: "noopener noreferrer", "aria-label": aria } as const;

  return (
    <div className="hw" ref={ref}>
      {layout === "bug" && (
        <div className="wa w-tab" data-state={c.state}>
          <div className="wa-brand"><Logo /></div>
          <a className="wa-main" key={vm.id} {...link}>
            <span className="wa-label"><Dot c={c} /><span>{c.label}</span></span>
            <span className="wa-line"><ScoreLine c={c} /></span>
          </a>
          {cycle && <button className="wa-next" type="button" aria-label="Next match" onClick={cycle}><Chevron /></button>}
        </div>
      )}

      {layout === "compact" && (
        <div className="wb w-tab" data-state={c.state}>
          <a className="wb-hit" {...link} />
          <div className="wb-brand"><Logo /></div>
          <div className="wb-body" key={vm.id}>
            <div className="wb-top">
              <Dot c={c} />
              <span className="wb-label">{c.label}</span>
              {cycle && nav && (
                <button className="wb-next" type="button" aria-label={`Next match (${nav.index + 1} of ${nav.total})`} onClick={cycle}>
                  <span>{nav.index + 1}/{nav.total}</span><Chevron small />
                </button>
              )}
            </div>
            <div className="wb-line"><ScoreLine c={c} /></div>
          </div>
        </div>
      )}

      {layout === "mini" && (
        <a className="wm w-tab" data-state={c.state} {...link}>
          <Logo />
          <span className="wm-sep" aria-hidden="true" />
          <Dot c={c} />
          {c.lead.score ? <><span className="s-code">{c.lead.code}</span><span className="s-score">{c.lead.score}</span></> : <span className="s-fixture">{c.teams[0].code}<i>v</i>{c.teams[1].code}</span>}
        </a>
      )}
    </div>
  );
};

export default EmbedHeaderWidget;
